<template>
    <div class="onboarding-wizard">
        <div class="wizard-container">
            <!-- Derslig Logo -->
            <div class="derslig-logo">
                <a href="/">
                    <img src="/img/landing/logo.svg" alt="Derslig">
                </a>
            </div>

            <!-- Progress Bar -->
            <div v-if="!['account_created', 'student_account_created', 'parent_completed'].includes(currentStep)" class="progress-container mb-8">
                <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
                </div>
            </div>

            <!-- Step Content -->
            <TransitionGroup name="slide-fade" mode="out-in">
                <!-- Step 1: User Type Selection -->
                <div v-if="currentStep === 'user_type'" key="user_type" class="step-content">
                    <h2 class="step-title">Eğitim yolculuğunda hangi rolü üstleniyorsun?</h2>
                    <p class="step-subtitle">Size en uygun deneyimi sunabilmek için önce sizi tanımak istiyoruz</p>
                    <div class="role-cards">
                        <div class="role-card" @click="selectUserType('teacher')" :class="{ active: formData.userType === 'teacher' }">
                            <div class="role-image">
                                <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                                </svg>
                            </div>
                            <button class="role-button">ÖĞRETMEN</button>
                        </div>
                        <div class="role-card" @click="selectUserType('student')" :class="{ active: formData.userType === 'student' }">
                            <div class="role-image">
                                <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                                </svg>
                            </div>
                            <button class="role-button primary">ÖĞRENCİ</button>
                        </div>
                        <div class="role-card" @click="selectUserType('parent')" :class="{ active: formData.userType === 'parent' }">
                            <div class="role-image">
                                <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                </svg>
                            </div>
                            <button class="role-button">VELİ</button>
                        </div>
                    </div>
                </div>

                <!-- Step 2: School Level Selection -->
                <div v-else-if="currentStep === 'school_level'" key="school_level" class="step-content">
                    <h2 class="step-title">
                        <span v-if="formData.userType === 'parent'">
                            Çocuğunuz hangi sınıf düzeyinde eğitim alıyor?
                        </span>
                        <span v-else-if="formData.userType === 'teacher'">
                            Hangi sınıf düzeyinde eğitim veriyorsunuz?
                        </span>
                        <span v-else>
                            Hangi sınıf düzeyinde eğitim alıyorsun veya veriyorsun?
                        </span>
                    </h2>
                    <div class="school-cards">
                        <div v-for="level in schoolLevels" :key="level.id" 
                             class="school-card" 
                             @click="selectSchoolLevel(level.id)"
                             :class="{ active: formData.schoolLevelId === level.id }">
                            <div class="school-image">
                                <svg class="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"></path>
                                </svg>
                            </div>
                            <button class="school-button" :class="{ primary: level.id === 1 }">
                                {{ level.name.toUpperCase() }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Step 3: Branch Selection (Sadece öğretmen için) -->
                <div v-else-if="currentStep === 'branch'" key="branch" class="step-content">
                    <h2 class="step-title">Hangi branşta öğretmenlik yapıyorsunuz?</h2>
                    
                    <div v-if="branches.length === 0" class="step-subtitle">
                        Branşlar yükleniyor...
                    </div>
                    <div v-else class="branch-grid">
                        <div v-for="(branch, index) in branches" :key="branch.id"
                             @click="selectBranch(branch.id)"
                             class="branch-card"
                             :class="{ 'active': formData.branchId === branch.id }">
                            
                            <div class="branch-content">
                                <h3 class="branch-name">{{ branch.name }}</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Step 4: Grade Selection (Sadece öğrenci için) -->
                <div v-else-if="currentStep === 'grade'" key="grade" class="step-content">
                    <h2 class="step-title">
                        <span v-if="formData.userType === 'parent'">
                            Çocuğunuz kaçıncı sınıfta?
                        </span>
                        <span v-else>
                            Kaçıncı sınıfsın?
                        </span>
                    </h2>
                    <div class="grade-grid">
                        <button v-for="grade in grades" :key="grade.id"
                                class="grade-button"
                                @click="selectGrade(grade.id)"
                                :class="{ active: formData.gradeId === grade.id }">
                            {{ grade.name }}
                        </button>
                    </div>
                </div>

                <!-- Step 5: Name Input -->
                <div v-else-if="currentStep === 'name'" key="name" class="step-content">
                    <h2 class="step-title">
                        <span v-if="formData.userType === 'parent'">
                            Size hitap edebilmek için öncelikle adınız ve soyadınızı öğrenebilir miyim?
                        </span>
                        <span v-else>
                            Sana hitap edebilmek için öncelikle adın ve soyadını öğrenebilir miyim?
                        </span>
                    </h2>
                    <p class="step-subtitle">
                        {{ formData.userType === 'parent' ? 'Çocuğunuz için en uygun eğitim planını oluşturalım' : 'Senin için en uygun eğitim planını oluşturalım' }}
                    </p>
                    <div class="name-inputs">
                        <div class="input-group">
                            <label class="input-label">
                                {{ formData.userType === 'parent' ? 'Adınız:' : 'Ad:' }}
                            </label>
                            <input v-model="formData.name" type="text" class="form-input" 
                                   :placeholder="formData.userType === 'parent' ? 'Adınızı giriniz' : 'Adınızı giriniz'">
                        </div>
                        <div class="input-group">
                            <label class="input-label">
                                {{ formData.userType === 'parent' ? 'Soyadınız:' : 'Soyad:' }}
                            </label>
                            <input v-model="formData.surname" type="text" class="form-input" 
                                   :placeholder="formData.userType === 'parent' ? 'Soyadınızı giriniz' : 'Soyadınızı giriniz'">
                        </div>
                    </div>
                </div>

                <!-- Step 6: Teacher Contact Info (Sadece öğretmen için) -->
                <div v-else-if="currentStep === 'teacher_contact'" key="teacher_contact" class="step-content">
                    <h2 class="step-title">İletişim bilgilerinizi öğrenebilir miyim?</h2>
                    <p class="step-subtitle">
                        Şifreniz telefon numaranıza gönderilecektir
                    </p>
                    <div class="name-inputs">
                        <div class="input-group">
                            <label class="input-label">E-posta Adresi:</label>
                            <input v-model="formData.email" type="email" class="form-input" placeholder="ornek@email.com">
                        </div>
                        <div class="input-group">
                            <label class="input-label">Telefon Numarası:</label>
                            <input v-model="formData.phone" 
                                   type="tel" 
                                   class="form-input" 
                                   placeholder="5XX XXX XX XX" 
                                   maxlength="17"
                                   @input="formatPhone"
                                   @keypress="validatePhoneInput">
                        </div>
                    </div>
                    <div v-if="verificationError" class="error-message">
                        {{ verificationError }}
                    </div>
                </div>

                <!-- Step 7: Teacher SMS Verification -->
                <div v-else-if="currentStep === 'teacher_verification'" key="teacher_verification" class="step-content">
                    <h2 class="step-title">Telefon Doğrulama</h2>
                    <p class="step-subtitle">
                        <strong>{{ formData.phone }}</strong> numaralı telefona gönderilen onay kodunu giriniz.
                    </p>
                    <div class="name-inputs">
                        <div class="input-group">
                            <label class="input-label">Onay Kodu:</label>
                            <input v-model="formData.verificationCode" type="text" class="form-input" placeholder="6 haneli kod">
                        </div>
                    </div>
                    <div v-if="verificationError" class="error-message">
                        {{ verificationError }}
                    </div>
                </div>

                <!-- Dynamic Questions -->
                <div v-else-if="currentQuestion" :key="'question_' + currentQuestion.id" class="step-content">
                    <h2 v-if="!showResponseInStep" class="step-title">{{ currentQuestion.question }}</h2>
                    
                    <!-- Response gösterimi -->
                    <div v-if="showResponseInStep && currentResponse" class="response-content">
                        <div v-if="currentResponse.text" class="response-text">
                            {{ currentResponse.text }}
                        </div>
                        
                        <div v-if="currentResponse.media_url" class="response-media">
                            <img v-if="currentResponse.media_type === 'image'" 
                                 :src="currentResponse.media_url" 
                                 alt="Geri Bildirim" 
                                 class="response-image">
                            <video v-else-if="currentResponse.media_type === 'video'" 
                                   :src="currentResponse.media_url" 
                                   controls autoplay
                                   class="response-video">
                            </video>
                        </div>
                    </div>
                    
                    <div v-else>
                        <div v-if="currentQuestion.type === 'single_choice'" class="options-grid">
                            <button v-for="(option, index) in (currentQuestion.options || [])" 
                                    :key="option.id || index"
                                    class="option-button"
                                    @click="selectOption(option.id)"
                                    :class="{ active: selectedOption === option.id }">
                                <span class="option-text">
                                    {{ option.text || option.title || option.name || option.option_text || `Seçenek ${index + 1}` }}
                                </span>
                            </button>
                            
                            <div v-if="!currentQuestion.options || currentQuestion.options.length === 0" 
                                 style="grid-column: 1 / -1; padding: 20px; text-align: center; color: #999;">
                                ⚠️ Bu soru için seçenek bulunamadı
                            </div>
                        </div>

                        <div v-else-if="currentQuestion.type === 'multiple_choice'" class="options-grid">
                            <label v-for="(option, index) in (currentQuestion.options || [])" 
                                   :key="option.id || index" 
                                   class="checkbox-option">
                                <input type="checkbox" 
                                       :value="option.id" 
                                       v-model="selectedOptions"
                                       class="checkbox-input">
                                <span class="checkbox-label">
                                    {{ option.text || option.title || option.name || option.option_text || `Seçenek ${index + 1}` }}
                                </span>
                            </label>
                            
                            <div v-if="!currentQuestion.options || currentQuestion.options.length === 0" 
                                 style="grid-column: 1 / -1; padding: 20px; text-align: center; color: #999;">
                                ⚠️ Bu soru için seçenek bulunamadı
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="currentStep === 'greeting'" key="greeting" class="step-content greeting-step">
                    <h2 class="step-title">
                        <span v-if="formData.userType === 'parent'">
                            Tanıştığıma memnun oldum {{ formData.name }}, çocuğunuzun eğitimi hakkında 
                            size en uygun önerileri sunabilmek için birkaç sorum olacak, hazırsan başlayalım mı?
                        </span>
                        <span v-else>
                            Tanıştığıma memnun oldum {{ formData.name }}, Seni daha iyi 
                            tanıyabilmek adına birkaç sorum olacak hazırsan başlayalım mı?
                        </span>
                    </h2>
                    <div class="mascot-container">
                        <div class="mascot-wrapper">
                            <svg class="mascot-image" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <p class="step-subtitle">
                        {{ formData.userType === 'parent' ? 'Çocuğunuz için en uygun eğitim planını oluşturalım' : '' }}
                    </p>
                </div>

                <!-- Account Created Step (Öğretmen için) -->
                <div v-else-if="currentStep === 'account_created'" key="account_created" class="step-content account-created-step">
                    <div class="success-icon">
                        <svg class="w-20 h-20 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    
                    <h2 class="step-title">🎉 Tebrikler! Hesabınız Başarıyla Oluşturuldu</h2>
                    
                    <div class="account-info">
                        <div class="info-box">
                            <h3 class="info-title">📧 E-posta Adresiniz</h3>
                            <p class="account-info-text">{{ formData.email }}</p>
                        </div>
                        
                        <div class="info-box">
                            <h3 class="info-title">📱 Telefon Numaranız</h3>
                            <p class="account-info-text">{{ formData.phone }}</p>
                        </div>
                        
                        <div class="info-box password-box">
                            <h3 class="info-title">🔐 Şifreniz SMS ile Gönderildi</h3>
                            <p class="account-info-text">Telefon numaranıza gönderilen şifre ile derslig.com hesabınıza giriş yapabilirsiniz.</p>
                        </div>
                    </div>
                    
                    <div class="redirect-info">

                        <p class="redirect-text">
                            Derslig hesabına giriş yapmak için aşağıdaki butona tıklayabilirsin.
                        </p>
                        <p class="manual-redirect">
                            <button @click="redirectNow" class="redirect-button">Hesabıma Git</button>
                        </p>
                    </div>
                </div>

                <!-- Registration Step (Sadece öğrenci için) -->
                <div v-else-if="currentStep === 'register'" key="register" class="step-content">
                    <h2 class="step-title">Öğrenme yolculuğuna kaldığın yerden devam edebilmek için bir üyelik oluşturalım!</h2>
                    <form class="register-form" @submit.prevent="completeOnboarding">
                        <div class="input-group">
                            <label class="input-label">Email Adresi:</label>
                            <input v-model="formData.email" type="email" class="form-input" placeholder="ornek@email.com" required>
                        </div>
                        <div class="input-group">
                            <label class="input-label">Şifre:</label>
                            <input v-model="formData.password" type="password" class="form-input" placeholder="••••••••" required minlength="8">
                            <!-- Şifre gereksinimlerini gösteren alan -->
                            <div class="password-requirements">
                                <div class="requirement-item" :class="{ 'valid': formData.password && formData.password.length >= 8 }">
                                    <span class="requirement-icon">{{ formData.password && formData.password.length >= 8 ? '✓' : '×' }}</span>
                                    <span class="requirement-text">En az 8 karakter</span>
                                </div>
                            </div>
                        </div>
                        <div class="input-group">
                            <label class="input-label">Şifre Tekrar:</label>
                            <input v-model="formData.passwordConfirmation" type="password" class="form-input" placeholder="••••••••" required>
                            <!-- Şifre eşleşme kontrolü -->
                            <div v-if="formData.passwordConfirmation" class="password-requirements">
                                <div class="requirement-item" :class="{ 'valid': formData.password === formData.passwordConfirmation }">
                                    <span class="requirement-icon">{{ formData.password === formData.passwordConfirmation ? '✓' : '×' }}</span>
                                    <span class="requirement-text">Şifreler eşleşiyor</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="verificationError" class="error-message">
                            {{ verificationError }}
                        </div>
                    </form>
                </div>

                <!-- Student Account Created Step -->
                <div v-else-if="currentStep === 'student_account_created'" key="student_account_created" class="step-content account-created-step">
                    <div class="success-icon">
                        <svg class="w-20 h-20 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    
                    <h2 class="step-title">🎉 Tebrikler! Hesabın Başarıyla Oluşturuldu</h2>
                    
                    <div class="account-info">
                        <div class="info-box password-box">
                            <h3 class="info-title">🎓 Öğrenci Hesabın Hazır!</h3>
                            <p class="account-info-text">Artık Derslig.com hesabına giriş yapabilir ve eğitim yolculuğuna başlayabilirsin.</p>
                        </div>
                    </div>
                    
                    <div class="redirect-info">
                        
                        <p class="redirect-text">
                            Derslig hesabına giriş yapmak için aşağıdaki butona tıklayabilirsin.
                        </p>
                        <p class="manual-redirect">
                            <button @click="redirectNow" class="redirect-button">Hesabıma Git</button>
                        </p>
                    </div>
                </div>

                <!-- Parent Completed Step (Veli için) -->
                <div v-else-if="currentStep === 'parent_completed'" key="parent_completed" class="step-content parent-completed-step">
                    <div class="success-icon">
                        <svg class="w-20 h-20 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    
                    <h2 class="step-title">🎉 Tebrikler {{ formData.name }}!</h2>
                    <p class="step-subtitle">Çocuğunuzun eğitim yolculuğunda size yardımcı olmaya hazırız</p>
                    
                    <div class="campaign-section">
                        <div class="campaign-card">
                            <div class="campaign-icon">🚀</div>
                            <h3>Derslig Pro ile Çocuğunuzun Potansiyelini Keşfedin!</h3>
                            <p>Binlerce öğretmen tarafından hazırlanmış kaliteli içeriklere erişim sağlayın</p>
                        </div>
                        
                        <div class="campaign-highlights">
                            <div class="highlight-item">
                                <span class="highlight-icon">✨</span>
                                <span>Tüm derslere sınırsız erişim</span>
                            </div>
                            <div class="highlight-item">
                                <span class="highlight-icon">📊</span>
                                <span>Detaylı performans raporları</span>
                            </div>
                            <div class="highlight-item">
                                <span class="highlight-icon">🎯</span>
                                <span>Kişiselleştirilmiş öğrenme planı</span>
                            </div>
                            <div class="highlight-item">
                                <span class="highlight-icon">👨‍🏫</span>
                                <span>Uzman öğretmen desteği</span>
                            </div>
                        </div>
                        
                        <div class="special-offer">
                            <div class="offer-badge">🔥 Özel Teklif</div>
                            <p class="offer-text">İlk ay <strong>%20 indirimli!</strong> Hemen başlayın ve çocuğunuzun başarısına yatırım yapın.</p>
                        </div>
                    </div>
                    
                    <!-- Paketleri İncele Butonu -->
                    <div class="action-section">
                        <button @click="goToPackages" class="packages-button">
                            <span class="button-icon">📦</span>
                            Paketleri İncele
                        </button>
                        <p class="action-subtitle">Çocuğunuz için en uygun paketi seçin</p>
                    </div>
                </div>


            </TransitionGroup>

            <div class="navigation-buttons">
                <button v-if="canGoBack" @click="goBack" class="nav-button back bg-gray-600 hover:bg-gray-700 text-white border border-gray-700 shadow-sm">
                    GERİ
                </button>
                <button v-if="canGoNext" @click="goNext" class="nav-button next" :class="{ primary: true }" :disabled="isLoading">
                    {{ isLoading ? 'İŞLENİYOR...' : (showResponseInStep ? 'DEVAM ET' : nextButtonText.toUpperCase()) }}
                </button>
            </div>
        </div>

        <transition name="modal">
            <div v-if="isLoading" class="loading-modal">
                <div class="loading-content">
                    <div class="loading-spinner">
                        <div class="spinner"></div>
                    </div>
                    <h3 class="loading-title">{{ loadingMessage }}</h3>
                    <p class="loading-subtitle">Bu işlem birkaç saniye sürebilir</p>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';

export default {
    name: 'OnboardingWizard',
    setup() {
        const urlUserType = ref(null);
        const currentStep = ref('user_type');
        const steps = ['user_type', 'school_level', 'branch', 'grade', 'name', 'teacher_contact', 'teacher_verification', 'greeting'];
        const stepIndex = ref(0);
        
        const startTime = ref(Date.now());
        const elapsedTime = ref(0);
        let timerInterval = null;
        
        const formData = ref({
            userType: null,
            schoolLevelId: null,
            branchId: null,
            gradeId: null,
            name: '',
            surname: '',
            email: '',
            phone: '',
            password: '',
            passwordConfirmation: '',
            verificationCode: ''
        });

        const schoolLevels = ref([]);
        const branches = ref([]);
        const grades = ref([]);
        const sessionId = ref('');
        const currentQuestion = ref(null);
        const selectedOption = ref(null);
        const selectedOptions = ref([]);
        const showResponseInStep = ref(false);
        const currentResponse = ref(null);
        const hasMoreQuestions = ref(false);
        const isLoading = ref(false);
        const loadingMessage = ref('');
        const verificationError = ref('');
        
        const redirectUrl = ref('');
        
        const answersHistory = ref({});
        const questionsHistory = ref([]);
        const currentQuestionIndex = ref(-1);

        const progressPercentage = computed(() => {
            const totalSteps = getStepsForUserType().length + (hasMoreQuestions.value ? 1 : 0) + 1;
            return Math.round((stepIndex.value + 1) / totalSteps * 100);
        });

        const getStepsForUserType = () => {
            if (formData.value.userType === 'teacher') {
                return ['user_type', 'school_level', 'branch', 'name', 'greeting', 'teacher_contact', 'teacher_verification'];
            } else if (formData.value.userType === 'student') {
                return ['user_type', 'school_level', 'grade', 'name', 'greeting'];
            } else if (formData.value.userType === 'parent') {
                return ['user_type', 'school_level', 'grade', 'name', 'greeting'];
            }
            return steps;
        };

        const canGoNext = computed(() => {
            if (showResponseInStep.value) {
                return true;
            }
            
            switch (currentStep.value) {
                case 'user_type':
                    return formData.value.userType !== null;
                case 'school_level':
                    return formData.value.schoolLevelId !== null;
                case 'branch':
                    return formData.value.branchId !== null;
                case 'grade':
                    return formData.value.gradeId !== null;
                case 'name':
                    return formData.value.name && formData.value.surname;
                case 'teacher_contact':
                    return formData.value.email && formData.value.phone;
                case 'teacher_verification':
                    return formData.value.verificationCode && formData.value.verificationCode.length === 6;
                case 'greeting':
                    return true;
                case 'register':
                    return formData.value.email && formData.value.password && 
                           formData.value.password === formData.value.passwordConfirmation &&
                           formData.value.password.length >= 8;
                default:
                    if (currentQuestion.value) {
                        return currentQuestion.value.type === 'single_choice' ? 
                               selectedOption.value !== null : 
                               selectedOptions.value.length > 0;
                    }
                    return false;
            }
        });

        const canGoBack = computed(() => {
            if (['account_created', 'student_account_created', 'parent_completed'].includes(currentStep.value)) {
                return false;
            }
            
            if (showResponseInStep.value) {
                return true;
            }
            
            if (urlUserType.value && stepIndex.value === 1) return false;
            
            if (currentQuestion.value && currentQuestionIndex.value > 0) return true;
            
            return stepIndex.value > 0 || currentQuestionIndex.value >= 0;
        });

        const nextButtonText = computed(() => {
            if (currentStep.value === 'teacher_contact') return 'SMS GÖNDER';
            if (currentStep.value === 'teacher_verification') return 'DOĞRULA';
            if (currentStep.value === 'greeting') return 'HAZIRIM!';
            if (currentStep.value === 'register') return 'KAYIT OL';
            return 'DEVAM ET';
        });

        const selectUserType = async (type) => {
            if (formData.value.userType && formData.value.userType !== type) {
                clearAllStorageData();
                
                formData.value = {
                    userType: type,
                    schoolLevelId: null,
                    branchId: null,
                    gradeId: null,
                    name: '',
                    surname: '',
                    email: '',
                    phone: '',
                    password: '',
                    passwordConfirmation: '',
                    verificationCode: ''
                };
            } else {
                formData.value.userType = type;
            }
            
            answersHistory.value = {};
            questionsHistory.value = [];
            currentQuestionIndex.value = -1;
            currentQuestion.value = null;
            
            proceedToNextStep();
        };

        const selectSchoolLevel = async (levelId) => {
            formData.value.schoolLevelId = levelId;
            await saveAnswer('school_level', levelId.toString());
            
            if (formData.value.userType === 'teacher') {
                await loadBranches(levelId);
            } else {
                await loadGrades(levelId);
            }
        };

        const selectBranch = async (branchId) => {
            formData.value.branchId = branchId;
            await saveAnswer('branch', branchId.toString());
        };

        const selectGrade = async (gradeId) => {
            formData.value.gradeId = gradeId;
            await saveAnswer('grade', gradeId.toString());
        };

        const selectOption = (optionId) => {
            selectedOption.value = optionId;
        };

        const goNext = async () => {
            if (showResponseInStep.value) {
                showResponseInStep.value = false;
                currentResponse.value = null;
                proceedToNextStep();
                return;
            }
            
            if (currentStep.value === 'name') {
                await saveAnswer('name', {
                    name: formData.value.name,
                    surname: formData.value.surname
                });
            } else if (currentStep.value === 'teacher_contact') {
                await sendTeacherSMS();
                return;
            } else if (currentStep.value === 'teacher_verification') {
                await verifyTeacherSMS();
                return;
            } else if (currentStep.value === 'register') {
                await completeOnboarding();
                return;
            }

            // Eğer soru varsa ve response gösterilecekse, önce cevabı kaydet ve sonraki soruya geç
            if (currentQuestion.value && currentQuestion.value.response_text) {
                // Önce cevabı kaydet
                const answer = currentQuestion.value.type === 'single_choice' ? 
                              selectedOption.value : 
                              selectedOptions.value;
                              
                answersHistory.value[currentQuestion.value.id.toString()] = answer;
                await saveAnswer(currentQuestion.value.id.toString(), answer);
                
                // Response'u hazırla
                currentResponse.value = {
                    text: currentQuestion.value.response_text,
                    media_url: currentQuestion.value.media_full_url || null,
                    media_type: currentQuestion.value.media_type || 'image'
                };
                
                // Sonraki soruyu yükle
                await loadNextQuestion();
                
                // Response'u göster
                showResponseInStep.value = true;
                return;
            }

            proceedToNextStep();
        };

        const sendTeacherSMS = async () => {
            try {
                isLoading.value = true;
                loadingMessage.value = 'SMS gönderiliyor...';
                
                let cleanPhone = formData.value.phone.replace(/\s/g, '');
                if (!cleanPhone.startsWith('0')) {
                    cleanPhone = '0' + cleanPhone;
                }
                
                const requestData = {
                    session_id: sessionId.value,
                    email: formData.value.email,
                    phone: cleanPhone,
                    name: formData.value.name,
                    surname: formData.value.surname,
                    school_level_id: formData.value.schoolLevelId,
                    branch_id: formData.value.branchId
                };
                
                const response = await axios.post('/api/onboarding/send-teacher-sms', requestData);

                if (response.data.success) {
                    verificationError.value = '';
                    proceedToNextStep();
                } else {
                    verificationError.value = response.data.message || 'SMS gönderilemedi';
                }
            } catch (error) {
                console.error('SMS gönderme hatası:', error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    verificationError.value = error.response.data.message;
                } else if (error.response && error.response.data && error.response.data.errors) {
                    const errors = error.response.data.errors;
                    const firstError = Object.values(errors)[0];
                    verificationError.value = Array.isArray(firstError) ? firstError[0] : firstError;
                } else if (error.response && error.response.status === 422) {
                    verificationError.value = 'Lütfen tüm alanları doğru doldurun. Eksik bilgi var.';
                } else {
                    verificationError.value = 'SMS gönderme sırasında bir hata oluştu';
                }
            } finally {
                isLoading.value = false;
                loadingMessage.value = '';
            }
        };

        const verifyTeacherSMS = async () => {
            try {
                isLoading.value = true;
                loadingMessage.value = 'Doğrulama yapılıyor...';
                
                const response = await axios.post('/api/onboarding/verify-teacher-sms', {
                    session_id: sessionId.value,
                    code: formData.value.verificationCode
                });

                if (response.data.success) {
                    verificationError.value = '';
                    await completeTeacherOnboarding();
                } else {
                    verificationError.value = response.data.message || 'Geçersiz doğrulama kodu';
                }
            } catch (error) {
                console.error('Doğrulama hatası:', error);
                verificationError.value = 'Doğrulama sırasında bir hata oluştu';
            } finally {
                isLoading.value = false;
                loadingMessage.value = '';
            }
        };

        const proceedToNextStep = async () => {
            if (currentStep.value === 'greeting') {
                if (formData.value.userType === 'teacher' || formData.value.userType === 'student' || formData.value.userType === 'parent') {
                    await loadNextQuestion();
                } else {
                    await completeOnboarding();
                }
            } else if (currentQuestion.value && !showResponseInStep.value) {
                const answer = currentQuestion.value.type === 'single_choice' ? 
                              selectedOption.value : 
                              selectedOptions.value;
                              
                answersHistory.value[currentQuestion.value.id.toString()] = answer;
                
                await saveAnswer(currentQuestion.value.id.toString(), answer);
                
                await loadNextQuestion();
            } else if (currentStep.value === 'teacher_verification') {
                await completeTeacherOnboarding();
            } else {
                const userSteps = getStepsForUserType();
                const currentStepIndex = userSteps.indexOf(currentStep.value);
                
                if (currentStepIndex < userSteps.length - 1) {
                    stepIndex.value++;
                    currentStep.value = userSteps[currentStepIndex + 1];
                } else {
                    currentQuestion.value = null;
                    hasMoreQuestions.value = false;
                    currentQuestionIndex.value = -1;
                    
                    if (formData.value.userType === 'teacher') {
                        currentStep.value = 'teacher_contact';
                        return;
                    } else if (formData.value.userType === 'student') {
                        currentStep.value = 'register';
                    } else if (formData.value.userType === 'parent') {
                        await completeOnboarding();
                    } else {
                        await completeOnboarding();
                    }
                }
            }

            selectedOption.value = null;
            selectedOptions.value = [];
        };

        const goBack = async () => {
            if (showResponseInStep.value) {
                showResponseInStep.value = false;
                currentResponse.value = null;
                return;
            }
            
            if (currentQuestion.value && currentQuestionIndex.value > 0) {
                currentQuestionIndex.value--;
                const previousQuestion = questionsHistory.value[currentQuestionIndex.value];
                currentQuestion.value = previousQuestion;
                currentStep.value = 'question_' + previousQuestion.id;
                
                const previousAnswer = answersHistory.value[previousQuestion.id.toString()];
                if (previousAnswer) {
                    if (previousQuestion.type === 'single_choice') {
                        selectedOption.value = previousAnswer;
                    } else {
                        selectedOptions.value = previousAnswer;
                    }
                }
                return;
            }
            
            if (currentQuestion.value && currentQuestionIndex.value === 0) {
                currentQuestion.value = null;
                currentQuestionIndex.value = -1;
                currentStep.value = 'greeting';
                selectedOption.value = null;
                selectedOptions.value = [];
                return;
            }
            
            if (stepIndex.value > 0) {
                const userSteps = getStepsForUserType();
                const currentStepIndex = userSteps.indexOf(currentStep.value);
                
                if (currentStepIndex > 0) {
                    stepIndex.value--;
                    currentStep.value = userSteps[currentStepIndex - 1];
                    currentQuestion.value = null;
                }
            }
        };



        const saveAnswer = async (step, answer) => {
            try {
                if (!sessionId.value) {
                    return;
                }
                
                await axios.post('/api/onboarding/save-answer', {
                    session_id: sessionId.value,
                    step: step,
                    answer: answer
                });
            } catch (error) {
                console.error('Error saving answer:', error);
            }
        };

        const loadBranches = async (schoolLevelId) => {
            try {
                branches.value = [];
                
                const response = await axios.get('/api/onboarding/branches', {
                    params: { school_level_id: schoolLevelId }
                });
                
                if (response.data && response.data.branches && Array.isArray(response.data.branches)) {
                    branches.value = response.data.branches.map(branch => ({
                        id: branch.id,
                        name: branch.name || `Branş ${branch.id}`,
                        ...branch
                    }));
                } else {
                    branches.value = [];
                }
            } catch (error) {
                console.error('Error loading branches:', error);
                branches.value = [];
            }
        };

        const loadGrades = async (schoolLevelId) => {
            try {
                const response = await axios.get('/api/onboarding/grades', {
                    params: { school_level_id: schoolLevelId }
                });
                grades.value = response.data.grades;
            } catch (error) {
                console.error('Error loading grades:', error);
            }
        };

        const loadNextQuestion = async () => {
            try {
                const response = await axios.get('/api/onboarding/next-question', {
                    params: {
                        session_id: sessionId.value,
                        current_step: currentStep.value,
                        user_type: formData.value.userType
                    }
                });

                if (response.data.question) {
                    currentQuestion.value = response.data.question;
                    hasMoreQuestions.value = response.data.has_more;
                    currentStep.value = 'question_' + response.data.question.id;
                    
                    const questionExists = questionsHistory.value.some(q => q.id === response.data.question.id);
                    if (!questionExists) {
                        questionsHistory.value.push(response.data.question);
                        currentQuestionIndex.value = questionsHistory.value.length - 1;
                    } else {
                        currentQuestionIndex.value = questionsHistory.value.findIndex(q => q.id === response.data.question.id);
                    }
                    
                    const previousAnswer = answersHistory.value[response.data.question.id.toString()];
                    if (previousAnswer) {
                        if (response.data.question.type === 'single_choice') {
                            selectedOption.value = previousAnswer;
                        } else {
                            selectedOptions.value = previousAnswer;
                        }
                    }
                } else {
                    currentQuestion.value = null;
                    hasMoreQuestions.value = false;
                    currentQuestionIndex.value = -1;
                    
                    if (formData.value.userType === 'teacher') {
                        currentStep.value = 'teacher_contact';
                        return;
                    } else if (formData.value.userType === 'student') {
                        currentStep.value = 'register';
                    } else if (formData.value.userType === 'parent') {
                        await completeOnboarding();
                    } else {
                        await completeOnboarding();
                    }
                }
            } catch (error) {
                console.error('Error loading next question:', error);
            }
        };

        const clearAllStorageData = () => {
            try {
                sessionStorage.clear();
                
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && (key.includes('onboarding') || key.includes('wizard') || key.includes('session'))) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
                
                formData.value = {
                    userType: null,
                    schoolLevelId: null,
                    branchId: null,
                    gradeId: null,
                    name: '',
                    surname: '',
                    email: '',
                    phone: '',
                    password: '',
                    passwordConfirmation: '',
                    verificationCode: ''
                };
                
                answersHistory.value = {};
                questionsHistory.value = [];
                currentQuestionIndex.value = -1;
                
            } catch (error) {
                console.error('Storage temizleme hatası:', error);
            }
        };

        const completeTeacherOnboarding = async () => {
            try {
                isLoading.value = true;
                loadingMessage.value = 'Öğretmen kaydınız tamamlanıyor...';
                
                const response = await axios.post('/api/onboarding/complete-teacher', {
                    session_id: sessionId.value,
                    duration_seconds: Math.floor((Date.now() - startTime.value) / 1000)
                });

                if (response.data.success) {
                    redirectUrl.value = response.data.redirect_url;
                    
                    currentStep.value = 'account_created';
                } else {
                    throw new Error(response.data.message || 'Bir hata oluştu');
                }
            } catch (error) {
                console.error('Error completing teacher onboarding:', error);
                
                let errorMessage = 'Kayıt tamamlama sırasında bir hata oluştu.';
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
                
                alert(errorMessage);
            } finally {
                isLoading.value = false;
                loadingMessage.value = '';
            }
        };



        const redirectNow = () => {
            clearAllStorageData();
            
            if (redirectUrl.value) {
                window.location.href = redirectUrl.value;
            }
        };

        const completeOnboarding = async () => {
            try {
                isLoading.value = true;
                loadingMessage.value = 'Tamamlanıyor...';
                
                if (formData.value.userType === 'teacher') {
                    await completeTeacherOnboarding();
                    return;
                }
                
                if (formData.value.userType === 'student') {
                    await completeStudentOnboarding();
                    return;
                }
                
                if (formData.value.userType === 'parent') {
                    let data = {
                        session_id: sessionId.value,
                        user_type: formData.value.userType,
                        duration_seconds: Math.floor((Date.now() - startTime.value) / 1000)
                    };

                    const response = await axios.post('/api/onboarding/complete', data);

                    if (response.data.success) {
                        currentStep.value = 'parent_completed';
                    } else {
                        throw new Error(response.data.message || 'Bir hata oluştu');
                    }
                    return;
                }
                
                let endpoint = '/api/onboarding/complete';
                let data = {
                    session_id: sessionId.value,
                    user_type: formData.value.userType,
                    duration_seconds: Math.floor((Date.now() - startTime.value) / 1000)
                };

                const response = await axios.post(endpoint, data);

                if (response.data.success) {
                    clearAllStorageData();
                    
                    if (response.data.redirect_url) {
                        window.location.href = response.data.redirect_url;
                    } else {
                        currentStep.value = 'complete';
                    }
                } else {
                    throw new Error(response.data.message || 'Bir hata oluştu');
                }
            } catch (error) {
                console.error('Error completing onboarding:', error);
                
                let errorMessage = 'Tamamlama sırasında bir hata oluştu.';
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response && error.response.data && error.response.data.errors) {
                    const errors = error.response.data.errors;
                    const firstError = Object.values(errors)[0];
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                }
                
                verificationError.value = errorMessage;
            } finally {
                isLoading.value = false;
                loadingMessage.value = '';
            }
        };
        
        const completeStudentOnboarding = async () => {
            try {
                if (formData.value.password !== formData.value.passwordConfirmation) {
                    verificationError.value = 'Şifreler eşleşmiyor';
                    return;
                }
                
                if (formData.value.password.length < 8) {
                    verificationError.value = 'Şifre en az 8 karakter olmalıdır';
                    return;
                }
                
                isLoading.value = true;
                loadingMessage.value = 'Öğrenci kaydın oluşturuluyor...';
                verificationError.value = '';
                
                const response = await axios.post('/api/onboarding/complete', {
                    session_id: sessionId.value,
                    user_type: 'student',
                    email: formData.value.email,
                    password: formData.value.password,
                    password_confirmation: formData.value.passwordConfirmation,
                    duration_seconds: Math.floor((Date.now() - startTime.value) / 1000),
                    name: formData.value.name,
                    surname: formData.value.surname,
                    school_level_id: formData.value.schoolLevelId,
                    grade_id: formData.value.gradeId
                });

                if (response.data.success) {
                    redirectUrl.value = response.data.redirect_url;
                    
                    currentStep.value = 'student_account_created';
                } else {
                    throw new Error(response.data.message || 'Bir hata oluştu');
                }
            } catch (error) {
                console.error('Error completing student onboarding:', error);
                
                let errorMessage = 'Kayıt oluşturma sırasında bir hata oluştu.';
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response && error.response.data && error.response.data.errors) {
                    const errors = error.response.data.errors;
                    const firstError = Object.values(errors)[0];
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                }
                
                verificationError.value = errorMessage;
            } finally {
                isLoading.value = false;
                loadingMessage.value = '';
            }
        };

        const startTimer = () => {
            startTime.value = Date.now();
            timerInterval = setInterval(() => {
                elapsedTime.value = Math.floor((Date.now() - startTime.value) / 1000);
            }, 1000);
        };

        const goToPackages = () => {
            let targetUrl = '/pro';
            
            if (formData.value.userType === 'parent' && formData.value.gradeId) {
                const selectedGrade = grades.value.find(grade => grade.id === formData.value.gradeId);
                
                if (selectedGrade && selectedGrade.name) {
                    const gradeMatch = selectedGrade.name.match(/(\d+)/);
                    
                    if (gradeMatch) {
                        const gradeNumber = gradeMatch[1];
                        
                        if (gradeNumber >= 1 && gradeNumber <= 12) {
                            targetUrl = `/pro/${gradeNumber}-sinif`;
                        }
                    }
                }
            }
            
            clearAllStorageData();
            window.location.href = targetUrl;
        };


        const formatPhone = (event) => {
            let value = event.target.value.replace(/\D/g, '');
            
            if (value.startsWith('0')) {
                value = value.substring(1);
            }
            
            if (value.length > 10) {
                value = value.substring(0, 10);
            }
            
            if (value.length >= 1) {
                let formatted = value;
                if (value.length > 3) {
                    formatted = value.substring(0, 3) + ' ' + value.substring(3);
                }
                if (value.length > 6) {
                    formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6);
                }
                if (value.length > 8) {
                    formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6, 8) + ' ' + value.substring(8);
                }
                
                formData.value.phone = formatted;
            } else {
                formData.value.phone = '';
            }
        };

        const validatePhoneInput = (event) => {
            const char = String.fromCharCode(event.which);
            if (!/[0-9]/.test(char)) {
                event.preventDefault();
            }
        };

        onMounted(async () => {
            try {
                const handleBeforeUnload = () => {
                    clearAllStorageData();
                };
                
                window.addEventListener('beforeunload', handleBeforeUnload);
                
                const urlParams = new URLSearchParams(window.location.search);
                const typeParam = urlParams.get('type');
                
                if (typeParam === 'ogretmen') {
                    urlUserType.value = 'teacher';
                    formData.value.userType = 'teacher';
                    currentStep.value = 'school_level';
                    stepIndex.value = 1;
                }
                
                const response = await axios.get('/api/onboarding/initial-data');
                schoolLevels.value = response.data.school_levels;
                sessionId.value = response.data.session_id;
                
                startTimer();
            } catch (error) {
                console.error('Error loading initial data:', error);
            }
        });
        
        onBeforeUnmount(() => {
            if (timerInterval) {
                clearInterval(timerInterval);
                timerInterval = null;
            }
            
            clearAllStorageData();
            window.removeEventListener('beforeunload', clearAllStorageData);
        });

        return {
            urlUserType,
            currentStep,
            formData,
            schoolLevels,
            branches,
            grades,
            currentQuestion,
            selectedOption,
            selectedOptions,
            showResponseInStep,
            currentResponse,
            progressPercentage,
            canGoNext,
            canGoBack,
            nextButtonText,
            selectUserType,
            selectSchoolLevel,
            selectBranch,
            selectGrade,
            selectOption,
            goNext,
            goBack,
            completeOnboarding,
            completeTeacherOnboarding,
            completeStudentOnboarding,
            isLoading,
            loadingMessage,
            answersHistory,
            questionsHistory,
            currentQuestionIndex,
            elapsedTime,
            verificationError,
            redirectUrl,
            redirectNow,
            clearAllStorageData,
            goToPackages,
            formatPhone,
            validatePhoneInput
        };
    }
};
</script>

<style>
.onboarding-wizard {
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;
    overflow: hidden;
}

.onboarding-wizard::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.05"><circle cx="30" cy="30" r="2"/></g></svg>') repeat;
    animation: float 20s ease-in-out infinite;
}

@keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
}

.wizard-container {
    max-width: 900px;
    width: 100%;
    background: white;
    border-radius: 20px;
    padding: 50px 40px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
    position: relative;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    overflow: visible;
}

.derslig-logo {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 5;
}

.derslig-logo img {
    height: 36px;
    width: auto;
}

.progress-container {
    margin-top: 20px;
    margin-bottom: 40px;
}

.progress-bar {
    height: 6px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    overflow: hidden;
    backdrop-filter: blur(5px);
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #e91e63, #f06292);
    transition: width 0.6s ease;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(233, 30, 99, 0.4);
}

.step-content {
    text-align: center;
    min-height: 400px;
    padding: 20px 0;
    position: relative;
    overflow: visible;
}

.step-title {
    font-size: 28px !important;
    font-weight: 700 !important;
    color: #2d3748 !important;
    margin-bottom: 16px !important;
    text-align: center !important;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.3 !important;
    letter-spacing: -0.5px !important;
}

.step-subtitle {
    font-size: 18px !important;
    color: #718096 !important;
    margin-bottom: 40px !important;
    text-align: center !important;
    font-weight: 400 !important;
    line-height: 1.5 !important;
}

.role-cards, .school-cards {
    display: flex;
    justify-content: center;
    gap: 32px;
    margin-top: 50px;
    flex-wrap: wrap;
}

.role-card, .school-card {
    cursor: pointer;
    transition: all 0.4s ease;
    position: relative;
    background: white;
    border-radius: 20px;
    padding: 30px 20px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    border: 2px solid transparent;
    min-width: 200px;
    text-align: center;
}

.role-card:hover, .school-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    border-color: rgba(233, 30, 99, 0.3);
}

.role-card.active, .school-card.active {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(233, 30, 99, 0.25);
    border-color: #e91e63;
    background: linear-gradient(135deg, #fff 0%, #fef7f7 100%);
}

.role-card.active::before {
    content: '✓';
    position: absolute;
    top: -8px;
    right: -8px;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #e91e63, #c2185b);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
    box-shadow: 0 4px 12px rgba(233, 30, 99, 0.4);
    animation: checkmark-bounce 0.6s ease;
}

@keyframes checkmark-bounce {
    0% { transform: scale(0); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
}

.role-image, .school-image {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 50%;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.role-card:hover .role-image,
.school-card:hover .school-image {
    transform: scale(1.1);
    background: linear-gradient(135deg, #e91e63, #c2185b);
}

.role-card:hover .role-image svg,
.school-card:hover .school-image svg {
    color: white !important;
    transform: scale(1.1);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.role-card.active .role-image,
.school-card.active .school-image {
    background: linear-gradient(135deg, #e91e63, #c2185b);
    transform: scale(1.15);
}

.role-card.active .role-image svg,
.school-card.active .school-image svg {
    color: white !important;
    transform: scale(1.1);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.role-image img, .school-image img {
    width: 80%;
    height: 80%;
    object-fit: contain;
}

.role-button, .school-button {
    padding: 12px 32px;
    border: none;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    color: #495057;
    font-weight: 600;
    border-radius: 25px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: hidden;
}

.role-card:hover .role-button,
.school-card:hover .school-button {
    background: linear-gradient(135deg, #e91e63, #c2185b);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(233, 30, 99, 0.3);
}

.role-card.active .role-button,
.school-card.active .school-button {
    background: linear-gradient(135deg, #e91e63, #c2185b);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(233, 30, 99, 0.4);
}

.role-button.primary, .school-button.primary {
    background: linear-gradient(135deg, #e91e63, #c2185b);
    color: white;
}

.role-button:hover, .school-button:hover {
    transform: translateY(-2px);
}

.role-button::before, .school-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.role-card:hover .role-button::before,
.school-card:hover .school-button::before {
    left: 100%;
}

.grade-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 16px;
    max-width: 500px;
    margin: 0 auto;
}

.grade-button {
    padding: 16px;
    border: 2px solid #e0e0e0;
    background: white;
    color: #333;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;
    display: inline-block;
    width: 100%;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.grade-button:hover {
    border-color: #e91e63;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(233, 30, 99, 0.1);
}

.grade-button.active {
    background: #e91e63;
    color: white;
    border-color: #e91e63;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(233, 30, 99, 0.3);
}

.name-inputs, .register-form {
    max-width: 500px;
    margin: 0 auto;
}

.input-group {
    margin-bottom: 24px;
    text-align: left;
}

.input-label {
    display: block;
    font-weight: 600;
    color: white;
    background: #e91e63;
    padding: 12px 16px;
    border-radius: 8px 8px 0 0;
    margin-bottom: -2px;
    font-size: 16px;
}

.form-input {
    width: 100%;
    padding: 16px;
    border: 2px solid #e91e63;
    border-radius: 0 0 8px 8px;
    font-size: 16px;
    outline: none;
    transition: border-color 0.2s;
    color: #2d3748;
    font-weight: 500;
}

.form-input:focus {
    border-color: #c2185b;
    box-shadow: 0 0 0 3px rgba(233, 30, 99, 0.1);
}

.form-input::placeholder {
    color: #a0aec0;
}

.options-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    max-width: 600px;
    margin: 0 auto;
}

.option-button {
    padding: 16px 24px;
    border: 2px solid #e0e0e0;
    background: white;
    color: #333;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s;
    min-height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.option-button:hover {
    border-color: #e91e63;
    color: #333;
}

.option-button.active {
    background: #e91e63;
    color: white;
    border-color: #e91e63;
}

.option-text {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    color: inherit;
    word-wrap: break-word;
    white-space: normal;
}

.checkbox-option {
    display: flex;
    align-items: center;
    padding: 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.checkbox-option:hover {
    border-color: #e91e63;
}

.checkbox-input {
    margin-right: 12px;
}

.checkbox-label {
    font-size: 16px;
}

.greeting-step {
    padding: 40px 0;
}

.mascot-container {
    margin: 40px 0;
    display: flex;
    justify-content: center;
    align-items: center;
}

.mascot-wrapper {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e91e63, #ad1457);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(233, 30, 99, 0.3);
}

.mascot-image {
    width: 80px;
    height: 80px;
    color: white;
    stroke-width: 2;
}



.navigation-buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 40px;
    gap: 16px;
}

.navigation-buttons:has(.back) {
    justify-content: space-between;
    gap: 0;
}

.nav-button {
    padding: 12px 40px;
    border-radius: 25px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.nav-button.back {
    border: none;
}

.nav-button.primary {
    background: #e91e63;
    color: white;
    border-color: #e91e63;
}

.nav-button:hover {
    transform: translateY(-2px);
}

.nav-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter-from {
    transform: translateX(20px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateX(-20px);
    opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.loading-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.loading-content {
    background: white;
    border-radius: 16px;
    padding: 40px;
    max-width: 400px;
    width: 90%;
    text-align: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.loading-spinner {
    margin-bottom: 24px;
    display: flex;
    justify-content: center;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid #e91e63;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-title {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
}

.loading-subtitle {
    font-size: 14px;
    color: #666;
    margin: 0;
}

.error-message {
    background: #fee;
    color: #c53030;
    padding: 12px;
    border-radius: 8px;
    margin-top: 12px;
    border: 1px solid #feb2b2;
    font-size: 14px;
    text-align: center;
}

.account-created-step {
    padding: 40px 0;
    text-align: center;
}

.account-created-step .step-title {
    font-size: 24px;
    font-weight: 600;
    color: #059669 !important;
    margin-bottom: 32px;
}

.success-icon {
    margin-bottom: 32px;
    animation: bounce-in 0.6s ease-out;
}

.account-info {
    margin-bottom: 40px;
    display: grid;
    gap: 24px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

.info-box {
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 24px;
    text-align: left;
    transition: all 0.3s ease;
}

.info-box:hover {
    border-color: #e91e63;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(233, 30, 99, 0.1);
}

.password-box {
    background: linear-gradient(135deg, #fff3e0, #f3e5f5);
    border-color: #e91e63;
}

.info-title {
    font-size: 18px;
    font-weight: 600;
    color: #333 !important;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
}

.account-info-text {
    color: #6b7280;
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
}

.redirect-info {
    margin-top: 40px;
}

.redirect-text {
    font-size: 16px;
    color: #666 !important;
    margin-bottom: 16px;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
}

.manual-redirect {
    text-align: center;
    color: #333 !important;
}

.redirect-button {
    background: #e91e63;
    color: white !important;
    border: none;
    padding: 12px 32px;
    border-radius: 25px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: underline;
}

.redirect-button:hover {
    background: #c2185b;
    transform: translateY(-1px);
}

@keyframes bounce-in {
    0% {
        transform: scale(0.3);
        opacity: 0;
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.branch-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    max-width: 800px;
    margin: 0 auto;
    padding: 20px 0;
}

.branch-card {
    padding: 20px;
    border: 2px solid #e0e0e0;
    background: white;
    border-radius: 12px;
    cursor: pointer;
    text-align: center;
    transition: all 0.3s ease;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.branch-card:hover {
    border-color: #e91e63;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(233, 30, 99, 0.1);
}

.branch-card.active {
    background: #e91e63;
    border-color: #e91e63;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(233, 30, 99, 0.3);
}

.branch-content {
    width: 100%;
}

.branch-name {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 5px 0;
    padding: 0;
    line-height: 1.4;
    word-wrap: break-word;
    white-space: normal;
}

.branch-card.active .branch-name {
    color: white;
}

.parent-completed-step {
    padding: 40px 0;
    text-align: center;
}

.parent-completed-step .step-title {
    font-size: 28px;
    font-weight: 600;
    color: #059669 !important;
    margin-bottom: 16px;
}

.parent-completed-step .step-subtitle {
    font-size: 18px;
    color: #666 !important;
    margin-bottom: 40px;
}

.campaign-section {
    margin-bottom: 40px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

.campaign-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 30px;
    border-radius: 16px;
    margin-bottom: 30px;
    text-align: center;
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.campaign-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.campaign-card h3 {
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 12px;
    color: white !important;
}

.campaign-card p {
    font-size: 16px;
    opacity: 0.9;
    color: white !important;
    margin: 0;
}

.campaign-highlights {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 30px;
}

.highlight-item {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    border-left: 4px solid #e91e63;
    transition: transform 0.2s ease;
}

.highlight-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.highlight-icon {
    font-size: 18px;
    margin-right: 12px;
}

.special-offer {
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
    color: white;
    padding: 20px;
    border-radius: 12px;
    position: relative;
    overflow: hidden;
}

.offer-badge {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    display: inline-block;
    margin-bottom: 12px;
}

.offer-text {
    font-size: 16px;
    margin: 0;
    color: white !important;
}

.offer-text strong {
    font-size: 20px;
    font-weight: 700;
}

.action-section {
    text-align: center;
    margin-top: 40px;
}

.packages-button {
    background: linear-gradient(135deg, #e91e63 0%, #ad1457 100%);
    color: white;
    border: none;
    padding: 16px 40px;
    border-radius: 30px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s ease;
    box-shadow: 0 6px 20px rgba(233, 30, 99, 0.3);
}

.packages-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(233, 30, 99, 0.4);
    background: linear-gradient(135deg, #c2185b 0%, #880e4f 100%);
}

.button-icon {
    font-size: 20px;
}

.action-subtitle {
    font-size: 14px;
    color: #666 !important;
    margin-top: 16px;
    margin-bottom: 0;
}

@media (max-width: 768px) {
    .campaign-highlights {
        grid-template-columns: 1fr;
    }
    
    .parent-completed-step .step-title {
        font-size: 24px;
    }
    
    .campaign-card {
        padding: 20px;
    }
    
    .campaign-card h3 {
        font-size: 20px;
    }
    
    .packages-button {
        padding: 14px 32px;
        font-size: 16px;
    }
}

.role-image svg, .school-image svg {
    width: 48px;
    height: 48px;
    color: #6b7280;
    transition: all 0.3s ease;
}

.role-card .role-image svg {
    color: #f59e0b;
}

.role-card:nth-child(2) .role-image svg {
    color: #e91e63;
}

.role-card:nth-child(3) .role-image svg {
    color: #8b5cf6;
}

.role-card:hover .role-image svg,
.school-card:hover .school-image svg,
.role-card.active .role-image svg,
.school-card.active .school-image svg {
    color: white !important;
    transform: scale(1.1);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

@media (max-width: 768px) {
    .wizard-container {
        margin: 10px;
        padding: 30px 20px;
        border-radius: 15px;
    }
    
    .role-cards {
        flex-direction: column;
        gap: 20px;
        align-items: center;
    }
    
    .role-card {
        max-width: 280px;
        width: 100%;
    }
    
    .step-title {
        font-size: 24px !important;
    }
    
    .step-subtitle {
        font-size: 16px !important;
    }
}

.response-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 20px 0;
    margin: 20px auto;
    width: 100%;
    max-width: 900px;
    animation: slideInUp 0.5s ease-out;
}

.response-media {
    margin-bottom: 40px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.response-image {
    width: auto;
    height: auto;
    max-width: 90%;
    border-radius: 16px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    object-fit: contain;
    display: block;
}

.response-video {
    width: 90%;
    height: auto;
    max-width: 500px;
    max-height: 300px;
    border-radius: 16px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    display: block;
}

.response-text {
    font-size: 18px;
    padding: 15px 0;
    line-height: 1.6;
    min-width: auto;
    max-width: 95%;
    width: 95%;
    margin: 0 auto 20px auto;
}

.response-continue-button {
    padding: 16px 50px;
    background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%);
    color: white;
    border: none;
    border-radius: 50px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 6px 18px rgba(233, 30, 99, 0.3);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 10px;
    align-self: center;
}

.response-continue-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(233, 30, 99, 0.4);
    background: linear-gradient(135deg, #c2185b 0%, #ad1457 100%);
}

@keyframes slideInUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@media (max-width: 768px) {
    .response-content {
        padding: 15px 0;
        margin: 20px 0;
        min-height: 300px;
    }
    
    .response-text {
        font-size: 18px;
        padding: 25px 20px;
        line-height: 1.6;
        min-width: auto;
        max-width: 95%;
        width: 95%;
        margin: 0 auto 30px auto;
    }
    
    .response-image, .response-video {
        max-width: 95%;
    }
    
    .response-continue-button {
        padding: 14px 40px;
        font-size: 14px;
    }
}

.password-requirements {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.requirement-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #6b7280;
}

.requirement-icon {
    margin-right: 8px;
    font-size: 16px;
}

.requirement-text {
    font-size: 14px;
}

.requirement-item.valid {
    color: #28a745;
}

.requirement-item.invalid {
    color: #dc3545;
}

</style> 